// User settings — provider choice, API key, display name, role.
//
// Persisted to IndexedDB (wrapper in src/lib/idb.ts). Why IDB rather than
// localStorage:
//   - Larger quota (localStorage is 5MB on Safari, evicted on storage
//     pressure; IDB gets the proper persistent quota).
//   - Cleaner async story.
//   - Future migration to encrypted storage (Web Crypto + key derived
//     from a passphrase or device-bound key) is possible without
//     changing call sites — same get/set surface.
//
// The API key is stored in plaintext for v1. Threat model: single-user
// device with OS-level encryption + lock screen (iPad / personal Mac);
// adding a passphrase prompt would gate every launch for negligible
// security gain. If a future audit decides we need wrapping, the wrap
// happens here, callers don't change.

import { create } from 'zustand';

import { idbGet, idbSet } from '../lib/idb';
import type { ProviderSlug } from '../core/providers';

const SETTINGS_KEY = 'user-settings';
const SCHEMA_VERSION = 1;

export interface UserSettings {
  schemaVersion: typeof SCHEMA_VERSION;
  setupComplete: boolean;
  /** Display name used in system prompts ("You are working with Sarah."). */
  displayName: string;
  /**
   * Free-text role / background. Goes into the user's per-machine
   * SOUL.md (generated from this on first launch) so the agent has
   * a sense of who Kenneth's colleague is without cross-pollinating
   * Kenneth's own memory.
   */
  role: string;
  providerSlug: ProviderSlug;
  apiKey: string;
  /** Optional model override. Empty string = use provider default. */
  model: string;
  createdAt: string;
}

const DEFAULT_SETTINGS: UserSettings = {
  schemaVersion: SCHEMA_VERSION,
  setupComplete: false,
  displayName: '',
  role: '',
  providerSlug: 'minimax',
  apiKey: '',
  model: '',
  createdAt: '',
};

interface SettingsStore {
  settings: UserSettings;
  /** True until the initial load finishes; UI can show a splash. */
  hydrating: boolean;
  /** Pull from IDB. Called once on app boot. */
  hydrate: () => Promise<void>;
  /** Persist a partial update to IDB and the in-memory store. */
  patch: (patch: Partial<UserSettings>) => Promise<void>;
  /** Clear everything (Settings → "Reset" button + DEV use). */
  reset: () => Promise<void>;
}

export const useSettings = create<SettingsStore>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  hydrating: true,

  hydrate: async () => {
    set({ hydrating: true });
    const stored = await idbGet<UserSettings>(SETTINGS_KEY);
    if (stored && stored.schemaVersion === SCHEMA_VERSION) {
      set({ settings: stored, hydrating: false });
    } else {
      // Either fresh install or schema bump that needs a migration. For
      // schemaVersion < SCHEMA_VERSION we'd run a migration here; until
      // we have a v2 schema we just fall back to defaults.
      set({ settings: DEFAULT_SETTINGS, hydrating: false });
    }
  },

  patch: async (patchObj) => {
    const next = {
      ...get().settings,
      ...patchObj,
      schemaVersion: SCHEMA_VERSION,
    } as UserSettings;
    if (!next.createdAt) next.createdAt = new Date().toISOString();
    await idbSet(SETTINGS_KEY, next);
    set({ settings: next });
  },

  reset: async () => {
    await idbSet(SETTINGS_KEY, DEFAULT_SETTINGS);
    set({ settings: DEFAULT_SETTINGS });
  },
}));
